import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Loader2, Image as ImageIcon, BookOpen, Trash2, TrendingUp, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", tags: "", cover_image: null });
  const [ai, setAi] = useState({ topic: "", keywords: "", tone: "informative and SEO-friendly", competitor: "" });
  const [generating, setGenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [insights, setInsights] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const load = () => api.get("/blogs").then(({ data }) => setBlogs(data));
  useEffect(() => { load(); }, []);

  const aiGenerate = async () => {
    if (!ai.topic) { toast.error("Topic required"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/blog", {
        topic: ai.topic,
        keywords: ai.keywords.split(",").map(s => s.trim()).filter(Boolean),
        tone: ai.tone,
        competitor: ai.competitor || null,
      });
      setForm({
        title: data.title || ai.topic,
        excerpt: data.excerpt || "",
        content: data.content_html || data.content_markdown || "",
        tags: (data.tags || []).join(", "),
        cover_image: form.cover_image,
      });
      toast.success("Blog draft generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const aiImage = async () => {
    if (!form.title && !ai.topic) { toast.error("Add title or topic first"); return; }
    setGeneratingImage(true);
    try {
      const { data } = await api.post("/ai/image", { prompt: `Modern editorial blog cover image for "${form.title || ai.topic}". Bold composition, vibrant, no text overlays, professional photography style.` });
      setForm({ ...form, cover_image: data.image });
      toast.success("Cover image ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image failed"); }
    finally { setGeneratingImage(false); }
  };

  const analyze = async () => {
    if (!ai.competitor) { toast.error("Enter competitor first"); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/ai/competitor", { competitor: ai.competitor, industry: "SaaS" });
      setInsights(data);
      toast.success("Competitor insights ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed"); }
    finally { setAnalyzing(false); }
  };

  const save = async () => {
    if (!form.title || !form.content) { toast.error("Title and content required"); return; }
    await api.post("/blogs", {
      title: form.title,
      excerpt: form.excerpt,
      content: form.content,
      tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
      cover_image: form.cover_image,
      status: "draft",
      competitor_url: ai.competitor || null,
    });
    setForm({ title: "", excerpt: "", content: "", tags: "", cover_image: null });
    toast.success("Blog saved");
    load();
  };

  const remove = async (id) => { await api.delete(`/blogs/${id}`); load(); };

  return (
    <div className="p-6 space-y-6" data-testid="blog-page">
      {/* Page Header */}
      <div className="rise flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Blog Studio</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>Rich editor · AI writer · Cover images · Competitor intel.</p>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total posts", value: blogs.length, color: "var(--brand)" },
          { label: "Drafts", value: blogs.filter(b => b.status === "draft").length, color: "var(--text-soft)" },
          { label: "Published", value: blogs.filter(b => b.status === "published").length, color: "var(--success)" },
        ].map(({ label, value, color }) => (
          <Card key={label} className="rise card-premium p-4 flex items-center gap-3"
            style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="text-2xl font-bold" style={{ color, fontFamily: "var(--font-sans)" }}>{value}</div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-soft)" }}>{label}</div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="write">
        <TabsList style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <TabsTrigger value="write" data-testid="tab-blog-write">Write</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-blog-insights">Competitor Insights</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-blog-library">Library ({blogs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="write" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 p-6 card-premium" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              {form.cover_image ? (
                <div className="relative mb-4">
                  <img src={form.cover_image} alt="" className="w-full h-48 object-cover rounded-md" />
                  <Button variant="outline" size="sm" onClick={() => setForm({ ...form, cover_image: null })} className="absolute top-2 right-2">Remove</Button>
                </div>
              ) : (
                <div className="w-full h-32 border-2 border-dashed rounded-xl flex items-center justify-center text-sm mb-4" style={{ borderColor: "var(--border)", color: "var(--text-soft)" }}>No cover image — use AI Image →</div>
              )}
              <Label>Title</Label>
              <Input data-testid="blog-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="text-xl font-semibold mb-3 sans-input" style={{ fontFamily: "Inter, sans-serif" }} />
              <Label>Excerpt</Label>
              <Input data-testid="blog-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mb-3 sans-input" style={{ fontFamily: "Inter, sans-serif" }} />
              <Label>Content</Label>
              <div className="mb-3">
                <RichTextEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} placeholder="Write your blog post…" minHeight={400} />
              </div>
              <Label>Tags</Label>
              <Input data-testid="blog-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="growth, saas, ai" />
              <div className="flex gap-2 mt-4">
                <Button data-testid="blog-save" onClick={save} className="text-white gap-2" style={{ background: "var(--brand)" }}>Save Draft</Button>
                <Button data-testid="blog-image" onClick={aiImage} disabled={generatingImage} variant="outline" className="gap-2">
                  {generatingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Generate Cover
                </Button>
              </div>
            </Card>

            <Card className="p-6 card-premium space-y-3" style={{ background: "color-mix(in srgb, var(--brand) 6%, var(--surface))", borderColor: "color-mix(in srgb, var(--brand) 25%, var(--border))" }}>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: "var(--brand)" }} />
                <h3 className="font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>AI Blog Writer</h3>
              </div>
              <div><Label>Topic</Label><Input data-testid="blog-ai-topic" value={ai.topic} onChange={(e) => setAi({ ...ai, topic: e.target.value })} placeholder="How AI is changing email marketing" /></div>
              <div><Label>Keywords (comma)</Label><Input data-testid="blog-ai-keywords" value={ai.keywords} onChange={(e) => setAi({ ...ai, keywords: e.target.value })} placeholder="ai, email, automation" /></div>
              <div><Label>Tone</Label><Input data-testid="blog-ai-tone" value={ai.tone} onChange={(e) => setAi({ ...ai, tone: e.target.value })} /></div>
              <div><Label>Competitor reference (optional)</Label><Input data-testid="blog-ai-competitor" value={ai.competitor} onChange={(e) => setAi({ ...ai, competitor: e.target.value })} placeholder="gohighlevel.com" /></div>
              <Button data-testid="blog-ai-generate" onClick={aiGenerate} disabled={generating} className="w-full text-white gap-2" style={{ background: "var(--brand)" }}>
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Write with Gemini
              </Button>
              <p className="text-xs text-slate-500 mt-2">Output is sans-serif HTML, no markdown # symbols.</p>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="insights" className="mt-4">
          <Card className="p-6 card-premium" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5" style={{ color: "var(--brand)" }} />
              <h3 className="font-bold text-lg" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Outrank a Competitor</h3>
            </div>
            <div className="flex gap-2 mb-6">
              <Input data-testid="insights-competitor" value={ai.competitor} onChange={(e) => setAi({ ...ai, competitor: e.target.value })} placeholder="gohighlevel.com or any competitor name" />
              <Button data-testid="insights-run" onClick={analyze} disabled={analyzing} className="text-white gap-2" style={{ background: "var(--brand)" }}>
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />} Analyze
              </Button>
            </div>
            {insights && (
              <div className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold mb-1 font-sans" style={{ color: "var(--text-soft)" }}>Summary</div>
                  <div className="text-sm" style={{ color: "var(--text)" }}>{insights.summary}</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl" style={{ background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)" }}><div className="text-xs font-sans font-semibold mb-2" style={{ color: "var(--success)" }}>STRENGTHS</div><ul className="text-sm space-y-1" style={{ color: "var(--text)" }}>{(insights.strengths || []).map((s, i) => <li key={i}>· {s}</li>)}</ul></div>
                  <div className="p-4 rounded-xl" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}><div className="text-xs font-sans font-semibold mb-2" style={{ color: "#EF4444" }}>WEAKNESSES</div><ul className="text-sm space-y-1" style={{ color: "var(--text)" }}>{(insights.weaknesses || []).map((s, i) => <li key={i}>· {s}</li>)}</ul></div>
                  <div className="p-4 rounded-xl" style={{ background: "var(--brand-dim)", border: "1px solid color-mix(in srgb, var(--brand) 25%, transparent)" }}><div className="text-xs font-sans font-semibold mb-2" style={{ color: "var(--brand)" }}>OPPORTUNITIES</div><ul className="text-sm space-y-1" style={{ color: "var(--text)" }}>{(insights.opportunities || []).map((s, i) => <li key={i}>· {s}</li>)}</ul></div>
                  <div className="p-4 rounded-xl" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)" }}><div className="text-xs font-sans font-semibold mb-2 flex items-center gap-1" style={{ color: "#D97706" }}><Lightbulb className="w-3 h-3" /> CONTENT IDEAS</div><ul className="text-sm space-y-1" style={{ color: "var(--text)" }}>{(insights.content_ideas || []).map((s, i) => <li key={i}>· {s}</li>)}</ul></div>
                </div>
              </div>
            )}
            {!insights && !analyzing && (
              <div className="text-center py-10" style={{ color: "var(--text-soft)" }}>Run analysis to surface strategic intel</div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="library" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {blogs.map((b, i) => (
              <Card key={b.id} data-testid={`blog-card-${i}`}
                className="card-premium overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
                style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                {b.cover_image
                  ? <img src={b.cover_image} alt="" className="w-full h-40 object-cover" />
                  : <div className="w-full h-40 flex items-center justify-center" style={{ background: "var(--brand-dim)" }}><BookOpen className="w-8 h-8" style={{ color: "var(--brand)" }} /></div>}
                <div className="p-4">
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(b.tags || []).slice(0, 3).map(t => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--brand-dim)", color: "var(--brand)" }}>{t}</span>
                    ))}
                  </div>
                  <h3 className="font-semibold mb-1" style={{ color: "var(--text)" }}>{b.title}</h3>
                  <p className="text-xs line-clamp-2 mb-3" style={{ color: "var(--text-soft)" }}>{b.excerpt || (b.content || "").replace(/<[^>]+>/g, "").slice(0, 120)}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: b.status === "published" ? "rgba(22,163,74,0.12)" : "var(--surface)", color: b.status === "published" ? "var(--success)" : "var(--text-soft)", border: "1px solid var(--border)" }}>{b.status}</span>
                    <Button variant="ghost" size="icon" onClick={() => remove(b.id)}>
                      <Trash2 className="w-4 h-4" style={{ color: "var(--text-xsoft)" }} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            {blogs.length === 0 && (
              <Card className="card-premium col-span-full p-16 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-20" style={{ color: "var(--text)" }} />
                <div className="text-lg font-semibold mb-2" style={{ color: "var(--text)" }}>No blog posts yet</div>
                <p className="text-sm" style={{ color: "var(--text-soft)" }}>Write your first post or use AI to generate one.</p>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
